import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../App.css';
import { API_URL } from '../Config';

function ClipEditModal({ isOpen, onClose, clip, videoUrl, onUpdateClipTimes }) {
  const [localStartTime, setLocalStartTime] = useState(clip ? (clip.startTime || 0) : 0);
  const [localEndTime, setLocalEndTime] = useState(clip ? (clip.endTime || 0) : 0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0); // Total video duration
  const [currentVideoTime, setCurrentVideoTime] = useState(0); // Current time of the video
  const [isCapturing, setIsCapturing] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (clip) {
      setLocalStartTime(clip.startTime || 0);
      setLocalEndTime(clip.endTime || 0);
      setCurrentVideoTime(clip.startTime || 0); // Set global seekbar to clip start
      if (videoRef.current) {
        videoRef.current.currentTime = clip.startTime || 0;
      }
    }
  }, [clip, videoUrl]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  const handleProgress = useCallback(() => {
    if (videoRef.current) {
      const playedSeconds = videoRef.current.currentTime;
      setCurrentVideoTime(playedSeconds); // Update global seekbar position

      // Loop the clip if playback goes beyond localEndTime
      if (isPlaying && playedSeconds >= localEndTime) {
        videoRef.current.currentTime = localStartTime; // Loop the clip
      }
    }
  }, [isPlaying, localEndTime, localStartTime]);

  const handleGlobalSeekChange = (e) => {
    if (videoRef.current) {
      const seekToTime = parseFloat(e.target.value);
      videoRef.current.currentTime = seekToTime;
      setCurrentVideoTime(seekToTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      videoRef.current.currentTime = localStartTime; // Seek to start of clip on load
    }
  };

  const handleUpdate = () => {
    onUpdateClipTimes(clip.id, localStartTime, localEndTime);
    onClose();
  };

  const handleCaptureFrame = async () => {
    if (!videoRef.current || !clip) return;
    
    try {
      setIsCapturing(true);
      
      // Send timestamp to backend for server-side frame capture
      const response = await fetch(`${API_URL}/timeline-items/${clip.id}/capture-frame?timestamp=${currentVideoTime}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Frame captured successfully:', result);
      
      // Show success message
      alert('Frame captured successfully!');
      
    } catch (error) {
      console.error('Error capturing frame:', error);
      alert(`Failed to capture frame: ${error.message}`);
    } finally {
      setIsCapturing(false);
    }
  };

  if (!isOpen || !clip) return null;

  // Defensive programming: ensure all values are valid numbers
  const safeLocalStartTime = Number.isFinite(localStartTime) ? localStartTime : 0;
  const safeLocalEndTime = Number.isFinite(localEndTime) ? localEndTime : 0;
  const safeCurrentVideoTime = Number.isFinite(currentVideoTime) ? currentVideoTime : 0;

  const clipDuration = Math.max(0, safeLocalEndTime - safeLocalStartTime);
  const displayedCurrentTime = Math.max(0, safeCurrentVideoTime - safeLocalStartTime);

  // Helper function to safely format numbers
  const safeToFixed = (value, decimals = 2) => {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '0.00';
    }
    return Number(value).toFixed(decimals);
  };

  try {
    return (
    <div className="modal-overlay">
      <div className="clip-edit-modal-large">
        <div className="player-wrapper">
          <video
            key={videoUrl} // Added key to force re-render when videoUrl changes
            ref={videoRef}
            src={videoUrl}
            width="100%"
            height="100%"
            controls={false} // We will use custom controls
            crossOrigin="anonymous" // Allow canvas export
            onTimeUpdate={handleProgress}
            onLoadedMetadata={handleLoadedMetadata}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        </div>
        <div className="compact-controls">
          <button onClick={handlePlayPause} className="control-btn">
            {isPlaying ? 'Pause' : 'Play'}
          </button>
          <button 
            onClick={handleCaptureFrame} 
            disabled={isCapturing}
            className="control-btn capture-btn"
          >
            {isCapturing ? 'Capturing...' : 'Capture Frame'}
          </button>
          <span className="time-display">{safeToFixed(displayedCurrentTime)}s / {safeToFixed(clipDuration)}s</span>
          <input
            type="range"
            min={safeLocalStartTime}
            max={safeLocalEndTime}
            step="0.01"
            value={safeCurrentVideoTime}
            onChange={handleGlobalSeekChange}
            className="seek-slider"
          />
          <div className="time-inputs-inline">
            <label className="time-label">
              Start:
              <input
                type="number"
                step="0.01"
                value={safeToFixed(localStartTime)}
                onChange={(e) => setLocalStartTime(parseFloat(e.target.value))}
                className="time-input"
              />
            </label>
            <label className="time-label">
              End:
              <input
                type="number"
                step="0.01"
                value={safeToFixed(localEndTime)}
                onChange={(e) => setLocalEndTime(parseFloat(e.target.value))}
                className="time-input"
              />
            </label>
          </div>
        </div>
        <div className="modal-actions">
          <button onClick={handleUpdate} className="modal-confirm-button">Update Clip</button>
          <button onClick={onClose} className="modal-cancel-button">Cancel</button>
        </div>
      </div>
    </div>
    );
  } catch (error) {
    console.error('Error in ClipEditModal:', error);
    return (
      <div className="modal-overlay">
        <div className="clip-edit-modal-large">
          <div className="modal-actions">
            <p>Error loading clip editor. Please try again.</p>
            <button onClick={onClose} className="modal-cancel-button">Close</button>
          </div>
        </div>
      </div>
    );
  }
}

export default ClipEditModal;